import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from "react";
import api from "@/services/api";

// ── Types ──────────────────────────────────────────────────────────────────────
export interface ChatNotification {
  id: string;
  occurrenceId: string;
  protocol: string;
  authorName: string;
  authorRole: string;
  messagePreview: string;
  commentType: "PUBLIC" | "INTERNAL" | "CITIZEN_RESPONSE" | "AGENT_RESPONSE";
  createdAt: string;
}

interface ChatNotificationsContextType {
  notifications: ChatNotification[];
  unreadCounts: Record<string, number>; // occurrenceId → count
  totalUnread: number;
  markAsRead: (occurrenceId: string) => void;
  setActiveOccurrenceId: (id: string | null) => void;
  dismissNotification: (notificationId: string) => void;
  dismissAll: () => void;
}

// ── Mock flag ──────────────────────────────────────────────────────────────────
// ⚠️  MOCK MODE: Altere para `false` quando o backend implementar os endpoints SSE
// Enquanto MOCK_SSE = true:
//   - Nenhuma conexão SSE é feita
//   - contagens de não lidos são iniciadas em {}
//   - O painel de notificações é funcional, mas só exibirá eventos manuais de teste
const MOCK_SSE = false;

// ── Context ────────────────────────────────────────────────────────────────────
const ChatNotificationsContext =
  createContext<ChatNotificationsContextType | null>(null);

export const useChatNotifications = () => {
  const ctx = useContext(ChatNotificationsContext);
  if (!ctx) {
    throw new Error(
      "useChatNotifications must be used within ChatNotificationsProvider",
    );
  }
  return ctx;
};

// ── Provider ───────────────────────────────────────────────────────────────────
export const ChatNotificationsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [notifications, setNotifications] = useState<ChatNotification[]>([]);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const sseRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // ID da ocorrência atualmente aberta no chat (para auto-marcar como lido)
  const activeOccurrenceIdRef = useRef<string | null>(null);

  const setActiveOccurrenceId = useCallback((id: string | null) => {
    activeOccurrenceIdRef.current = id;
  }, []);

  const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0);

  // ── Mark as read ─────────────────────────────────────────────────────────────
  const markAsRead = useCallback(async (occurrenceId: string) => {
    // Atualiza estado local de forma otimista
    setUnreadCounts((prev) => {
      const next = { ...prev };
      delete next[occurrenceId];
      return next;
    });

    try {
      await api.post(`/occurrences/${occurrenceId}/comments/read`);
    } catch (e) {
      console.warn("[ChatNotifications] Falha ao marcar como lido", e);
    }
  }, []);

  const dismissNotification = useCallback((notificationId: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
  }, []);

  const dismissAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const addNotification = useCallback(
    (payload: Omit<ChatNotification, "id">) => {
      // Se o chat desta ocorrência está aberto agora, marca como lido em vez de incrementar
      if (activeOccurrenceIdRef.current === payload.occurrenceId) {
        // Sem badge, sem painel — apenas chama o endpoint para manter o backend sincronizado
        api
          .post(`/occurrences/${payload.occurrenceId}/comments/read`)
          .catch(() => {});
        return;
      }

      const notification: ChatNotification = {
        ...payload,
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      };

      setNotifications((prev) => {
        return [notification, ...prev].slice(0, 5);
      });

      setUnreadCounts((prev) => ({
        ...prev,
        [payload.occurrenceId]: (prev[payload.occurrenceId] || 0) + 1,
      }));
    },
    [],
  );

  const fetchUnreadCounts = useCallback(async () => {
    if (MOCK_SSE) return; // mock: backend ainda não implementado

    try {
      const { data } = await api.get("/occurrences/comments/unread-counts");
      // Formato esperado: { counts: Array<{ occurrenceId: string; count: number }> }
      const map: Record<string, number> = {};
      (data.counts || []).forEach(
        ({ occurrenceId, count }: { occurrenceId: string; count: number }) => {
          map[occurrenceId] = count;
        },
      );
      setUnreadCounts(map);
    } catch (e) {
      console.warn("[ChatNotifications] Falha ao buscar contagens não lidas", e);
    }
  }, []);

  // ── Conexão SSE ───────────────────────────────────────────────────────────────
  const connectSSE = useCallback(() => {
    if (MOCK_SSE) return; // mock: sem conexão até o backend estar pronto

    const token = localStorage.getItem("duas-vidas-token");
    if (!token) return;

    const baseUrl =
      import.meta.env.VITE_BASE_URL || "http://localhost:3000/api";
    // Token via query param pois EventSource não suporta headers customizados
    const url = `${baseUrl}/occurrences/comments/stream?token=${encodeURIComponent(token)}`;

    const source = new EventSource(url);
    sseRef.current = source;

    // Evento: nova mensagem chegou
    source.addEventListener("new_comment", (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data) as {
          occurrenceId: string;
          authorId: string;
          protocol: string;
          authorName: string;
          authorRole: string;
          content: string;
          commentType: string;
          createdAt: string;
        };

        // Não notifica o próprio usuário
        const userDataStr = localStorage.getItem("duas-vidas-user");
        const currentUser = userDataStr ? JSON.parse(userDataStr) : null;
        if (currentUser?.id && data.authorId === currentUser.id) return;

        addNotification({
          occurrenceId: data.occurrenceId,
          protocol: data.protocol,
          authorName: data.authorName,
          authorRole: data.authorRole,
          messagePreview:
            data.content.length > 100
              ? `${data.content.slice(0, 100)}...`
              : data.content,
          commentType: data.commentType as ChatNotification["commentType"],
          createdAt: data.createdAt,
        });
      } catch (err) {
        console.error("[ChatNotifications] Erro ao parsear evento SSE", err);
      }
    });

    // Evento: contagens de não lidos sincronizadas
    source.addEventListener("unread_counts", (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data) as {
          counts: Array<{ occurrenceId: string; count: number }>;
        };
        const map: Record<string, number> = {};
        data.counts.forEach(({ occurrenceId, count }) => {
          map[occurrenceId] = count;
        });
        setUnreadCounts(map);
      } catch (err) {
        console.error(
          "[ChatNotifications] Erro ao parsear evento unread_counts",
          err,
        );
      }
    });

    // Evento: keep-alive — ignorado
    source.addEventListener("ping", () => {});

    source.onerror = () => {
      source.close();
      sseRef.current = null;
      // Reconecta após 5 segundos
      reconnectTimerRef.current = setTimeout(() => {
        if (localStorage.getItem("duas-vidas-token")) connectSSE();
      }, 5000);
    };
  }, [addNotification]);

  useEffect(() => {
    fetchUnreadCounts();
    connectSSE();

    return () => {
      sseRef.current?.close();
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    };
  }, [fetchUnreadCounts, connectSSE]);

  return (
    <ChatNotificationsContext.Provider
      value={{
        notifications,
        unreadCounts,
        totalUnread,
        markAsRead,
        setActiveOccurrenceId,
        dismissNotification,
        dismissAll,
      }}
    >
      {children}
    </ChatNotificationsContext.Provider>
  );
};
